import React, { useMemo } from "react";
import { Avatar, Icon } from "@medentic/polaris-react";
import { CheckSmallIcon } from "@medentic/polaris-icons";
import { ROOTS } from "@app/config/constants";
import { setLocalStorage } from "@shared/lib";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HealthcareCompany, HealthcareCompanyType } from "@shared/types/commonTypes";
import { useGetSingleCompanyDetails } from "@shared/hooks/common/useGetSingleCompanyDetails";
import { MedenticTopBar, MenuAction } from "@shared/ui";

type EventTopBarProps = {
  onNavigationToggle: () => void;
};

const EMPTY_EVENT_ORGANIZERS: HealthcareCompany[] = [];

const EventTopBar: React.FC<EventTopBarProps> = ({ onNavigationToggle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const eventOrganizersData = queryClient.getQueryData<HealthcareCompany[]>([
    "healthcare-companies",
    HealthcareCompanyType.EVENT_ORGANIZER,
  ]);
  const eventOrganizers = useMemo(
    () => eventOrganizersData ?? EMPTY_EVENT_ORGANIZERS,
    [eventOrganizersData]
  );
  const params = useParams();
  // Exclude "add" from being treated as a valid event organizer ID
  const selectedEventOrganizerId = params.eventOrganizerId !== "add" ? params.eventOrganizerId : undefined;

  // Get detailed company information using the hook
  const { company } = useGetSingleCompanyDetails(selectedEventOrganizerId, {
    refetchOnMount: false, // Use cached data for performance
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  const selectedEventOrganizer =
    eventOrganizers.find((e) => e.id === selectedEventOrganizerId) ||
    eventOrganizers[0];

  // Prepare organization menu items
  const organizationMenuItems: MenuAction[] = useMemo(() => {
    // Filter by the actual selected organizer's ID to prevent duplicates
    const sortedEventOrganizers = eventOrganizers
      .filter((eventOrganizer) => eventOrganizer.id !== selectedEventOrganizer?.id)
      .sort((a, b) => a.name.localeCompare(b.name));

    const eventOrganizersToList = [
      selectedEventOrganizer,
      ...sortedEventOrganizers,
    ].filter((e) => !!e);

    return eventOrganizersToList.slice(0, 4).map((e) => {
      // Create a stable reference for the icon function
      const IconComponent = () => (
        <Avatar
          initials={e.avatar.initials}
          name={e.name}
          source={e.avatar.image}
        />
      );
      
      return {
        content: e.name,
        icon: IconComponent,
        onAction: () => {
          // User explicitly selected this event organizer, navigate directly to it
          setLocalStorage("__LAST_VISITED_E_ID", e.id);
          navigate(`${ROOTS.EVENTS}/${e.id}`);
        },
        active: e.id === selectedEventOrganizerId,
        suffix: e.id === selectedEventOrganizerId && (
          <Icon source={CheckSmallIcon} />
        ),
      };
    });
  }, [eventOrganizers, selectedEventOrganizer, selectedEventOrganizerId, navigate]);

  return (
    <MedenticTopBar
      onNavigationToggle={onNavigationToggle}
      userMenuName={company?.name ?? selectedEventOrganizer?.name ?? ""}
      userMenuInitials={company?.avatar?.initials ?? selectedEventOrganizer?.avatar.initials}
      userMenuAvatar={company?.avatar?.image ?? selectedEventOrganizer?.avatar.image}
      organizationMenuItems={organizationMenuItems}
      allOrganizationsLabel={t("topbar.eventOrganizers")}
      allOrganizationsRoute={ROOTS.EVENTS}
      showSearchField={true}
      showLocaleSwitcher={true}
      showPIIToggle={true}
      showUserProfileInMenu={true}
    />
  );
};

export default React.memo(EventTopBar);
